import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAddShowcaseSite, useDetectShowcaseSite, useRemoveShowcaseSite, useUpdateShowcaseSite } from "@/hooks/useShowcase";
import { MAX_SITES, checkLineOf, hostOf, isDevHost, shortDateOf, siteKeyOf, visibleSitesOf } from "./showcaseSetupModel";

const fail = (error) => toast.error(error.message);

const PageField = ({ brokerageId, site, onDone }) => {
  const update = useUpdateShowcaseSite(brokerageId);
  const [value, setValue] = useState(site.pageUrl || "");
  const [error, setError] = useState("");

  useEffect(() => {
    setValue(site.pageUrl || "");
    setError("");
  }, [site.id, site.pageUrl]);

  const submit = (event) => {
    event.preventDefault();
    update.mutate(
      { siteId: site.id, pageUrl: value.trim() || null },
      {
        onSuccess: () => {
          toast.success("Page saved. We check it once a day.");
          onDone();
        },
        onError: (failure) => {
          const field = failure.fields?.pageUrl;
          setError(
            field === "otherSite"
              ? `Use a page on ${site.host}.`
              : field
                ? `Enter the page address, like ${site.host}/buyers.`
                : failure.message,
          );
        },
      },
    );
  };

  return (
    <form className="showcase-setup__page" onSubmit={submit} noValidate>
      <div className="showcase-setup__row">
        <input
          id={`page-url-${site.id}`}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError("");
          }}
          placeholder={`${site.host}/buyers`}
          inputMode="url"
          aria-label={`Page on ${site.host} where the buyers show`}
          aria-invalid={Boolean(error)}
          autoFocus
        />
        <button type="submit" className="showcase-setup__link" disabled={update.isPending || value.trim() === (site.pageUrl || "")}>
          Save
        </button>
        <button type="button" className="showcase-setup__quiet" onClick={onDone}>
          Cancel
        </button>
      </div>
      {error ? (
        <p className="showcase-setup__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
};

const SiteRow = ({ brokerageId, site, reading, removeSite }) => {
  const [editing, setEditing] = useState(false);
  const line = checkLineOf(site);
  return (
    <li className="showcase-setup__site">
      <div className="showcase-setup__line showcase-setup__line--site">
        <span className="showcase-setup__host">{site.host}</span>
        {reading ? (
          <span className="showcase-setup__status" role="status">
            Reading the site…
          </span>
        ) : (
          <span className={`showcase-setup__status is-${line.status}`}>{line.text}</span>
        )}
        <span className="showcase-setup__actions">
          <button type="button" className="showcase-setup__quiet" onClick={() => setEditing(!editing)} aria-expanded={editing}>
            {line.action || "Change page"}
          </button>
          <button
            type="button"
            className="showcase-setup__quiet showcase-setup__quiet--muted"
            disabled={removeSite.isPending}
            aria-label={`Remove ${site.host}`}
            onClick={() => removeSite.mutate(site.id, { onError: fail })}
          >
            Remove
          </button>
        </span>
      </div>
      {editing ? <PageField brokerageId={brokerageId} site={site} onDone={() => setEditing(false)} /> : null}
    </li>
  );
};

const SightingRow = ({ sighting, canAdd, addSite }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="showcase-setup__line showcase-setup__line--site">
      <span className="showcase-setup__host">{sighting.host}</span>
      <span className="showcase-setup__status is-missing">Tried on {shortDateOf(sighting.lastSeenAt)}</span>
      <span className="showcase-setup__actions">
        {!canAdd ? null : confirming ? (
          <>
            <button
              type="button"
              className="showcase-setup__quiet"
              disabled={addSite.isPending}
              onClick={() => addSite.mutate(sighting.host, { onSuccess: () => setConfirming(false), onError: fail })}
            >
              Yes, it&apos;s mine
            </button>
            <button type="button" className="showcase-setup__quiet showcase-setup__quiet--muted" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="showcase-setup__quiet" onClick={() => setConfirming(true)}>
            Add
          </button>
        )}
      </span>
    </li>
  );
};

const SiteList = ({ brokerageId, showcase }) => {
  const addSite = useAddShowcaseSite(brokerageId);
  const removeSite = useRemoveShowcaseSite(brokerageId);
  const detect = useDetectShowcaseSite(brokerageId);
  const [host, setHost] = useState("");
  const [hostError, setHostError] = useState("");
  const [reading, setReading] = useState(null);
  const sites = visibleSitesOf(showcase.sites, isDevHost(window.location.hostname));
  const [adding, setAdding] = useState(false);
  const sightings = showcase.sightings || [];

  const submit = (event) => {
    event.preventDefault();
    const clean = hostOf(host);
    if (!clean || !clean.includes(".")) {
      setHostError("Enter a website address, like yourbrokerage.com.");
      return;
    }
    if (sites.some((site) => siteKeyOf(site.host) === siteKeyOf(clean))) {
      setHostError(`${clean} is already on your list.`);
      return;
    }
    addSite.mutate(clean, {
      onSuccess: (data) => {
        setHost("");
        setHostError("");
        setAdding(false);
        const added = (data?.showcase?.sites || []).find((site) => site.host === clean);
        if (!added) return;
        setReading(added.id);
        detect.mutate(added.id, { onSettled: () => setReading(null), onError: fail });
      },
      onError: (error) => setHostError(error.fields?.host ? "Enter a website address, like yourbrokerage.com." : error.message),
    });
  };

  return (
    <div className="showcase-setup__part">
      <h5 className="showcase-setup__subhead">Your websites</h5>
      {sites.length ? (
        <ul className="showcase-setup__lines">
          {sites.map((site) => (
            <SiteRow key={site.id} brokerageId={brokerageId} site={site} reading={reading === site.id} removeSite={removeSite} />
          ))}
        </ul>
      ) : (
        <p className="showcase-setup__hint">The cards only load on the websites you add&nbsp;here.</p>
      )}
      {sites.length && !adding && sites.length < MAX_SITES ? (
        <button type="button" className="showcase-setup__link showcase-setup__add-domain" onClick={() => setAdding(true)}>
          Add another domain
        </button>
      ) : null}
      {sites.length < MAX_SITES && (!sites.length || adding) ? (
        <form className="showcase-setup__row" onSubmit={submit} noValidate>
          <input
            value={host}
            onChange={(event) => {
              setHost(event.target.value);
              setHostError("");
            }}
            placeholder="yourbrokerage.com"
            inputMode="url"
            aria-label="Website address"
            aria-invalid={Boolean(hostError)}
          />
          <button type="submit" className="showcase-setup__link" disabled={!host.trim() || addSite.isPending}>
            {addSite.isPending ? "Adding…" : "Add website"}
          </button>
        </form>
      ) : (
        <p className="showcase-setup__hint">Remove a website to add another&nbsp;one.</p>
      )}
      {hostError ? (
        <p className="showcase-setup__error" role="alert">
          {hostError}
        </p>
      ) : null}
      {sightings.length ? (
        <>
          <h5 className="showcase-setup__subhead">Tried to load on websites you did not&nbsp;add</h5>
          <ul className="showcase-setup__lines">
            {sightings.map((sighting) => (
              <SightingRow key={sighting.host} sighting={sighting} canAdd={sites.length < MAX_SITES} addSite={addSite} />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
};

export default SiteList;
